import { useEffect } from 'react'

import type { GivingCampaignOverviewDto } from '@percent/workplace-giving/api/campaigns/campaign.types'
import { getCampaignsByGroupId } from '@percent/workplace-giving/api/campaigns/getCampaignGroups/getCampaignsByGroupId'
import { useInfiniteQuery, useLogger, useWindowDimensions } from '@percent/workplace-giving/common/hooks'

export type UseGivingCampaignGroupRow = Readonly<{
  campaigns: GivingCampaignOverviewDto[]
  skeletonData: string[]
  tilesAmount: number
  isPending: boolean
  isFetchingNextPage: boolean
  isFetchingPreviousPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  hasPreviousPage: boolean
  fetchPreviousPage: () => void
}>

export const SCREEN_WIDTH_BREAKPOINT = 1280

export const useGivingCampaignGroupRow = (groupId: string): UseGivingCampaignGroupRow => {
  const { logError } = useLogger()
  const { width: windowWidth } = useWindowDimensions()

  const isWindowWide = windowWidth > SCREEN_WIDTH_BREAKPOINT
  const tilesAmount = isWindowWide ? 4 : 3

  const {
    data,
    isPending,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isError,
    error
  } = useInfiniteQuery(['givingCampaignGroup', groupId, { pageSize: 25 }], getCampaignsByGroupId, {
    staleTime: 1000 * 20
  })

  useEffect(() => {
    if (isError && error) {
      logError(error)
    }
  }, [isError, error, logError])

  return {
    campaigns: data?.pages.flatMap(page => page.data) ?? [],
    skeletonData: Array.from({ length: tilesAmount }, (_, i) => i.toString()),
    tilesAmount,
    isPending,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage
  }
}
